import React, { useState, useEffect, useCallback, useRef, useMemo, lazy, Suspense } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useToast, Modal, Button, useLayout, Spinner, Icon, Input } from '../../components/ds';
import api from '../../api/client';
import { CDN } from '../../config/cdn';
import './CreativeEditor.css';

// Lazy-load AlphaConverterModal — ffmpeg.wasm 不進主 bundle
const AlphaConverterModal = lazy(() =>
  import('../../features/alphaConverter').then(m => ({ default: m.AlphaConverterModal }))
);

// --- tracking constants ---
const CLICK_URL_KEY = 'click_url';
const KNOWN_PARTIES = ['DCM', 'IAS', 'LnData', 'DoubleVerify', 'Dar', 'custom'];
const EVENT_OPTIONS = ['impression', 'view'];

// --- type coercion helper ---
function coerceValue(value, prop) {
  if (value === undefined || value === null) return value;
  const fmt = (prop.format || prop.type || '').toLowerCase();
  if (fmt === 'boolean') {
    if (typeof value === 'boolean') return value;
    if (value === 'true') return true;
    if (value === 'false') return false;
    return Boolean(value);
  }
  if (fmt === 'number' || fmt === 'float' || fmt === 'integer') {
    if (typeof value === 'number') return value;
    const n = Number(value);
    return isNaN(n) ? 0 : n;
  }
  return value;
}

// --- meta_list loader ---
let cachedMetaList = null;

async function loadMetaList() {
  if (cachedMetaList) return cachedMetaList;
  const resp = await fetch(
    CDN.META_LIST
  );
  const text = await resp.text();
  const jsonStr = text
    .replace(/^export\s+const\s+meta_list\s*=\s*/, '')
    .replace(/;\s*$/, '');
  // eslint-disable-next-line no-new-func
  const fn = new Function('return ' + jsonStr);
  cachedMetaList = fn();
  console.log('[CreativeEditor] meta_list loaded, components:', Object.keys(cachedMetaList));
  return cachedMetaList;
}

// --- template_textfield parser ---
// The template_textfield is JS code like:
//   const creativeConfig = { "properties": {}, "template": { ... } };
//   export default creativeConfig;
// It contains arrow functions like: "_onPrepared": (x) => { context.comp.x = x; }
// and colon-prefixed strings like: "context": ":context", "adbox": ":basic_adbox"
function parseTemplateTextfield(textfield) {
  if (!textfield) {
    console.warn('[CreativeEditor] parseTemplateTextfield: empty textfield');
    return null;
  }
  try {
    let code = textfield;
    console.log('[CreativeEditor] parseTemplateTextfield: input length =', code.length);

    // Step 1: Remove const/export wrapper
    code = code.replace(/const\s+creativeConfig\s*=\s*/, '');
    code = code.replace(/export\s+default\s+creativeConfig;?\s*$/, '');

    // Step 2: Remove arrow function properties like "_onPrepared": (x) => { ... }
    // and any other function properties that contain arrow functions
    // These can have nested content so we use a balanced brace approach
    code = removeArrowFunctions(code);

    // Step 3: Clean up trailing/double commas left by removal
    code = code.replace(/,\s*,/g, ',');  // Remove double commas
    code = code.replace(/,(\s*[}\]])/g, '$1');  // Remove trailing commas

    code = code.trim();
    if (code.endsWith(';')) code = code.slice(0, -1);

    console.log('[CreativeEditor] parseTemplateTextfield: cleaned code (first 200 chars):', code.substring(0, 200));

    // Step 4: Parse as JSON — the template_textfield uses double-quoted strings
    // which is valid JSON after removing functions
    let config;
    try {
      config = JSON.parse(code);
    } catch (jsonErr) {
      // Fallback: try eval with Function
      console.log('[CreativeEditor] JSON.parse failed, trying Function eval:', jsonErr.message);
      // eslint-disable-next-line no-new-func
      const fn = new Function('const context = { comp: {} }; return ' + code);
      config = fn();
    }

    console.log('[CreativeEditor] parseTemplateTextfield: success, template keys =',
      config?.template ? Object.keys(config.template) : 'NO TEMPLATE');
    return config;
  } catch (e) {
    console.error('[CreativeEditor] parseTemplateTextfield FAILED:', e);
    console.error('[CreativeEditor] Raw textfield:', textfield.substring(0, 300));
    return null;
  }
}

// Remove arrow function properties from JSON-like code
// Strategy: replace entire "key": (args) => { ... } blocks with empty string
// This handles both "_onPrepared": (x) => { ... } and multi-line functions
function removeArrowFunctions(code) {
  // Pattern explanation:
  // "[^"]+"\s*:\s* — match "key":
  // (?:\([^)]*\)|\w+) — match (args) or bareword
  // \s*=>\s* — match =>
  // \{[^{}]*(?:\{[^{}]*\}[^{}]*)*\} — match balanced braces (nested up to 2 levels)
  // This regex handles most common arrow function patterns in template_textfield
  
  // First pass: remove simple arrow functions (no nested braces)
  let result = code.replace(
    /"[^"]+"\s*:\s*(?:\([^)]*\)|\w+)\s*=>\s*\{[^{}]*\}\s*,?/g,
    ''
  );
  
  // Second pass: remove arrow functions with one level of nesting
  // Match: "key": (args) => { ... { ... } ... }
  result = result.replace(
    /"[^"]+"\s*:\s*(?:\([^)]*\)|\w+)\s*=>\s*\{(?:[^{}]|\{[^{}]*\})*\}\s*,?/g,
    ''
  );
  
  return result;
}

// --- Property categorisation ---
import { CATEGORY_ORDER, CATEGORY_CONFIG } from '../../config/categoryConfig';
import { parseVariants, getDefaultVariant } from '../../utils/parseVariants';

// (CategoryLabel removed — section header now renders icon box + title + subtitle inline)

function categorisePropertyWithConfig(prop, fieldConfig) {
  // 1. field_config override (from Admin)
  if (fieldConfig?.category) return fieldConfig.category;
  // 2. meta_list category
  return categorisePropertyDefault(prop);
}

function categorisePropertyDefault(prop) {
  if (prop.category) {
    const cat = prop.category;
    // Accept both original lowercase and new capitalized forms
    const normalized = cat.charAt(0).toUpperCase() + cat.slice(1).toLowerCase();
    if (CATEGORY_ORDER.includes(normalized)) return normalized;
    if (CATEGORY_ORDER.includes(cat)) return cat;
  }
  const t = (prop.type || '').toLowerCase();
  const key = (prop.key || '').toLowerCase();
  if (t === 'image' || t === 'video' || t === 'medias') return 'Medias';
  if (t === 'form' || key.includes('track') || key.includes('pixel')) return 'Trackings';
  if (key.includes('url') || key.includes('link')) return 'URLs';
  return 'Advance';
}

// Build config_object for saving to PAC.
// PAC editor requires config_object to be valid JSON — all arrow function handlers
// must be stored as quoted string values (e.g. "_onPrepared":"(tracker) => {}").
// Strategy: extract the raw template block from rawTextfield (preserving PAC original
// structure verbatim), then in-place replace every "_on*": arrowFn with a JSON string value.
// This never calls buildPreviewConfig so no preview-specific fields (context, injected
// default handlers) pollute the save payload.
function buildConfigObject(properties, rawTextfield) {
  if (!rawTextfield) {
    return JSON.stringify({ properties });
  }

  // --- Step 1: extract rawTemplateBlock from rawTextfield using balanced-brace matching ---
  const templateKeyIdx = rawTextfield.indexOf('"template"');
  if (templateKeyIdx === -1) {
    return JSON.stringify({ properties });
  }
  const colonIdx = rawTextfield.indexOf(':', templateKeyIdx + 10);
  const braceStart = rawTextfield.indexOf('{', colonIdx);
  if (braceStart === -1) {
    return JSON.stringify({ properties });
  }
  let depth = 0;
  let braceEnd = -1;
  for (let i = braceStart; i < rawTextfield.length; i++) {
    if (rawTextfield[i] === '{') depth++;
    else if (rawTextfield[i] === '}') {
      depth--;
      if (depth === 0) { braceEnd = i; break; }
    }
  }
  if (braceEnd === -1) {
    return JSON.stringify({ properties });
  }
  const rawTemplateBlock = rawTextfield.substring(braceStart, braceEnd + 1);

  // --- Step 2: in-place replace all "_on*": arrowFn  with JSON string values ---
  // Match key pattern, then use balanced-brace to extract the full arrow function.
  // Arrow functions may be:  (args) => { ... }  or  arg => { ... }
  const handlerKeyRegex = /"(_on\w+)"\s*:\s*/g;
  let result = rawTemplateBlock;
  let offset = 0; // tracks how much result has grown/shrunk vs rawTemplateBlock

  // Collect all replacements first (on rawTemplateBlock positions) to avoid offset drift
  const replacements = []; // { start, end, newText } in rawTemplateBlock coords

  let m;
  while ((m = handlerKeyRegex.exec(rawTemplateBlock)) !== null) {
    const valueStart = m.index + m[0].length; // where the arrow function starts in rawTemplateBlock
    // Determine arrow function end via balanced-brace matching
    // Arrow fn may start with ( or an identifier; find the opening { of body
    const arrowIdx = rawTemplateBlock.indexOf('=>', valueStart);
    if (arrowIdx === -1) continue;
    const bodyBraceStart = rawTemplateBlock.indexOf('{', arrowIdx);
    if (bodyBraceStart === -1) continue;
    let d = 0;
    let bodyBraceEnd = -1;
    for (let i = bodyBraceStart; i < rawTemplateBlock.length; i++) {
      if (rawTemplateBlock[i] === '{') d++;
      else if (rawTemplateBlock[i] === '}') {
        d--;
        if (d === 0) { bodyBraceEnd = i; break; }
      }
    }
    if (bodyBraceEnd === -1) continue;

    const arrowFnRaw = rawTemplateBlock.substring(valueStart, bodyBraceEnd + 1).trim();
    // JSON-stringify the arrow function string (handles \, ", \n, \t etc.)
    const jsonStringValue = JSON.stringify(arrowFnRaw);

    replacements.push({ start: valueStart, end: bodyBraceEnd + 1, newText: jsonStringValue });
  }

  // Apply replacements in reverse order (back-to-front) to keep earlier positions valid
  replacements.sort((a, b) => b.start - a.start);
  let stringifiedBlock = rawTemplateBlock;
  for (const rep of replacements) {
    stringifiedBlock =
      stringifiedBlock.substring(0, rep.start) +
      rep.newText +
      stringifiedBlock.substring(rep.end);
  }

  // --- Step 3: assemble and self-verify ---
  const candidate = `{"properties":${JSON.stringify(properties)},"template":${stringifiedBlock}}`;
  try {
    JSON.parse(candidate);
  } catch (e) {
    console.error('[buildConfigObject] JSON.parse self-check failed:', e, '\nstringifiedBlock:', stringifiedBlock);
    return JSON.stringify({ properties });
  }
  return candidate;
}

// Build preview config for PAC preview.html postMessage.
// Arrow functions must be STRING values (not real functions).
// Every component needs "context": ":context".
// Every template key needs a corresponding properties entry.
function buildPreviewConfig(properties, rawTextfield) {
  if (!rawTextfield) return { properties, template: {} };

  // Parse the textfield to get the template structure (with real arrow functions)
  const parsed = parseTemplateTextfield(rawTextfield);
  if (!parsed || !parsed.template) return { properties, template: {} };

  // Also extract arrow functions from raw text as strings
  // Strategy: re-parse the raw textfield but keep arrow functions as strings
  const template = {};

  // Get the raw template block from textfield
  const templateKeyIdx = rawTextfield.indexOf('"template"');
  if (templateKeyIdx === -1) return { properties, template: {} };
  const colonIdx = rawTextfield.indexOf(':', templateKeyIdx + 10);
  const braceStart = rawTextfield.indexOf('{', colonIdx);
  if (braceStart === -1) return { properties, template: {} };

  // For each component in parsed template, rebuild with string callbacks
  for (const [compKey, compDef] of Object.entries(parsed.template)) {
    const entry = {};

    // Copy non-function properties from parsed template
    for (const [k, v] of Object.entries(compDef)) {
      if (k.startsWith('_on')) continue; // skip, will extract from raw text
      entry[k] = v;
    }

    // Ensure context
    if (!entry.context) entry.context = ':context';

    // Extract _onXxx callbacks as strings from raw textfield
    // Find this component's block in the raw text
    const compKeyPattern = `"${compKey}"`;
    let compIdx = rawTextfield.indexOf(compKeyPattern, templateKeyIdx);
    if (compIdx !== -1) {
      const compBraceStart = rawTextfield.indexOf('{', compIdx);
      if (compBraceStart !== -1) {
        // Find matching close brace
        let depth = 0;
        let compBraceEnd = -1;
        for (let i = compBraceStart; i < rawTextfield.length; i++) {
          if (rawTextfield[i] === '{') depth++;
          else if (rawTextfield[i] === '}') {
            depth--;
            if (depth === 0) { compBraceEnd = i; break; }
          }
        }
        if (compBraceEnd !== -1) {
          const compBlock = rawTextfield.substring(compBraceStart, compBraceEnd + 1);
          // Extract _onXxx patterns: "_onSomething": (args) => { ... }
          const callbackRegex = /"(_on\w+)"\s*:\s*((?:\([^)]*\)|\w+)\s*=>\s*\{)/g;
          let match;
          while ((match = callbackRegex.exec(compBlock)) !== null) {
            const cbName = match[1];
            const cbStart = match.index + match[0].indexOf(match[2]);
            // Find the arrow function body (balance braces)
            const arrowStart = compBlock.indexOf('=>', match.index);
            const bodyStart = compBlock.indexOf('{', arrowStart);
            let d = 0;
            let bodyEnd = -1;
            for (let i = bodyStart; i < compBlock.length; i++) {
              if (compBlock[i] === '{') d++;
              else if (compBlock[i] === '}') {
                d--;
                if (d === 0) { bodyEnd = i; break; }
              }
            }
            if (bodyEnd !== -1) {
              // Extract full arrow function as string
              const arrowIdx = compBlock.indexOf(match[2], match.index);
              const fullCallback = compBlock.substring(arrowIdx, bodyEnd + 1).trim();
              entry[cbName] = fullCallback;
            }
          }
        }
      }
    }

    // Default _onPrepared if not found: store self in context
    if (!entry._onPrepared) {
      entry._onPrepared = `(${compKey}) => { context.comp.${compKey} = ${compKey}; }`;
    }

    template[compKey] = entry;
  }

  // Ensure every template key has a properties entry
  const fullProperties = { ...properties };
  for (const key of Object.keys(template)) {
    if (!fullProperties[key]) fullProperties[key] = {};
  }

  return { template, properties: fullProperties };
}

// Parse existing creative config_object to extract properties values
function parseExistingConfig(configObject) {
  if (!configObject) return null;
  try {
    // config_object may be JSON or JS (with arrow functions)
    let config;
    try {
      config = JSON.parse(configObject);
    } catch {
      // Try removing JS wrapper: "const creativeConfig = {...}; export default creativeConfig;"
      let code = configObject
        .replace(/^const\s+creativeConfig\s*=\s*/, '')
        .replace(/;\s*export\s+default\s+creativeConfig;?\s*$/, '');
      code = removeArrowFunctions(code);
      code = code.replace(/,\s*,/g, ',').replace(/,(\s*[}\]])/g, '$1').trim();
      if (code.endsWith(';')) code = code.slice(0, -1);
      config = JSON.parse(code);
    }
    return config?.properties || null;
  } catch (e) {
    console.error('[parseExistingConfig] failed:', e);
    return null;
  }
}

// (categoriseProperty moved above as categorisePropertyWithConfig / categorisePropertyDefault)

// Parse column definitions from meta_list column format.
// rawColumns: array of { colKey: { type, description, ... } } objects
// fallback: returned when rawColumns is empty/invalid
function parseColumns(rawColumns, fallback) {
  if (!rawColumns || !Array.isArray(rawColumns) || rawColumns.length === 0) {
    return fallback;
  }
  const columns = [];
  for (const colGroup of rawColumns) {
    if (typeof colGroup !== 'object') continue;
    for (const [colKey, colDef] of Object.entries(colGroup)) {
      columns.push({
        key: colKey,
        label: colDef.description || colKey,
        type: colDef.type || 'string',
        format: colDef.format || 'string',
        accepted: colDef.accepted || [],
        options: colDef.options || [],
        default: colDef.default || '',
        optional: colDef.optional || false,
      });
    }
  }
  return columns.length > 0 ? columns : fallback;
}

export default function CreativeEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editUUID = searchParams.get('edit'); // present when editing an existing creative
  const { addToast } = useToast();
  const { sidebarCollapsed } = useLayout();

  const [ad, setAd] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateUUID, setSelectedTemplateUUID] = useState('');
  // AS-210: user-supplied creative display name (optional; empty → backend auto-naming)
  const [creativeName, setCreativeName] = useState('');
  const [metaList, setMetaList] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Component properties: { [componentKey]: { componentName, properties: [...] } }
  const [components, setComponents] = useState({});
  // Raw template JS text from template_textfield (preserves arrow functions for save)
  const [rawTemplateText, setRawTemplateText] = useState('');
  // Form values: { [componentKey]: { [propertyKey]: value } }
  const [formValues, setFormValues] = useState({});
  // Upload state: { [`${compKey}-${propKey}`]: 'uploading' | null }
  const [uploadingFields, setUploadingFields] = useState({});
  // Hidden values (from template_overrides): same structure as formValues
  const [hiddenValues, setHiddenValues] = useState({});
  // Variant state — AS-050 / AS-171
  // parsedVariants: derived from selected template's field_config.variants
  const [parsedVariants, setParsedVariants] = useState([]);
  // selectedVariantKey: the variant_key chosen by user (new-ad) or resolved from ad.variant_key (edit).
  // AS-171: identity is now key-based; name is derived by lookup when needed.
  const [selectedVariantKey, setSelectedVariantKey] = useState('');
  // Accordion open state — from CATEGORY_CONFIG defaultOpen
  const [openSections, setOpenSections] = useState(() => {
    const state = {};
    for (const key of CATEGORY_ORDER) {
      state[key] = CATEGORY_CONFIG[key].defaultOpen;
    }
    return state;
  });

  // AS-220: distinguish "parse failed" (null config / no config.template) from
  // "parse succeeded but all fields hidden" (visible=false in field_config).
  // allFieldsHidden=true → blocking error, do NOT show JSON textarea.
  const [allFieldsHidden, setAllFieldsHidden] = useState(false);

  // Tracking UI state
  const [trackingModalOpen, setTrackingModalOpen] = useState(false);
  const [trackingModalCtx, setTrackingModalCtx] = useState(null); // { compKey, prop }

  // Alpha Converter Modal state
  const [alphaConverterOpen, setAlphaConverterOpen] = useState(false);
  const [alphaConverterCtx, setAlphaConverterCtx] = useState(null); // { compKey, propKey }
  const [trackingInput, setTrackingInput] = useState('');
  const [trackingParty, setTrackingParty] = useState('custom');
  const [trackingEvent, setTrackingEvent] = useState('impression');
  const [trackingEditIdx, setTrackingEditIdx] = useState(null);
  const [trackingViewTarget, setTrackingViewTarget] = useState(null); // { item, detected }

  // mediasFields: all medias-type properties across all components, used by URLs section.
  // Depends on components (structure), not formValues — recalculate only when template changes.
  const mediasFields = useMemo(() => {
    const fields = [];
    for (const [compKey, comp] of Object.entries(components)) {
      for (const prop of comp.properties) {
        if ((prop.type || '').toLowerCase() === 'medias') {
          fields.push({ compKey, prop });
        }
      }
    }
    return fields;
  }, [components]);

  const metaListRef = useRef(null);
  // Pending creative values when editing an existing creative (replaces window.__pendingCreativeValues)
  const pendingCreativeValuesRef = useRef(null);
  const editInitDoneRef = useRef(false);

  // Lock body scroll — editor uses fixed positioning for full viewport control
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  // Load ad + templates + meta_list
  useEffect(() => {
    async function init() {
      setLoading(true);
      try {
        const [adData, tplData, ml] = await Promise.all([
          api.getPortalAd(id),
          api.getPortalTemplates(id),
          loadMetaList(),
        ]);
        console.log('[CreativeEditor] init: ad =', adData?.name, ', templates count =', tplData?.length);
        console.log('[CreativeEditor] init: templates =', tplData?.map(t => ({
          uuid: t.uuid,
          name: t.name,
          has_textfield: !!t.template_textfield,
          textfield_len: t.template_textfield?.length || 0,
        })));
        setAd(adData);
        // AS-171: in edit mode, set selectedVariantKey from ad.variant_key.
        // If variant_key is null (legacy ad), leave selectedVariantKey as '' — the
        // edit-mode dropdown will fall back to displaying ad.variant_name snapshot.
        if (editUUID && adData?.variant_key) {
          setSelectedVariantKey(adData.variant_key);
        }
        setTemplates(tplData);
        setMetaList(ml);
        metaListRef.current = ml;

        // If editing an existing creative (edit=<uuid> in URL), load its config
        if (editUUID) {
          try {
            const existing = await api.getCreativeDetail(id, editUUID);
            if (existing) {
              // AS-210: pre-fill creativeName with current name so user sees it in the input
              if (existing.creative_name) {
                setCreativeName(existing.creative_name);
              }
              // PAC single creative API uses template_id (not template_uuid)
              const templateId = existing.template_id;
              console.log('[CreativeEditor] Found existing creative:', editUUID,
                ', template_id:', templateId,
                ', has config_object:', !!existing.config_object);
              if (templateId) {
                if (existing.config_object) {
                  const existingConfig = parseExistingConfig(existing.config_object);
                  if (existingConfig) {
                    console.log('[CreativeEditor] Parsed existing config, keys:', Object.keys(existingConfig));
                    pendingCreativeValuesRef.current = existingConfig;
                    editInitDoneRef.current = false; // 標記需要在 useEffect 中重新觸發 handleTemplateChange
                  } else {
                    console.warn('[CreativeEditor] parseExistingConfig returned null');
                  }
                } else {
                  console.warn('[CreativeEditor] existing creative has no config_object');
                }
                setSelectedTemplateUUID(templateId);
              }
            }
          } catch (e) {
            console.log('[CreativeEditor] Could not load existing creative:', e.message);
          }
        }
      } catch (err) {
        console.error('[CreativeEditor] init failed:', err);
        setError(err.message || '載入失敗');
      } finally {
        setLoading(false);
      }
    }
    init();
  }, [id]);

  // When template is selected, parse components and build form.
  // variantKeyOverride: if provided, select this variant_key instead of the default.
  const handleTemplateChange = useCallback(
    (uuid, variantKeyOverride) => {
      setSelectedTemplateUUID(uuid);
      if (!uuid) {
        setComponents({});
        setRawTemplateText('');
        setFormValues({});
        setHiddenValues({});
        setParsedVariants([]);
        setSelectedVariantKey('');
        setAllFieldsHidden(false);
        return;
      }

      const tpl = templates.find((t) => t.uuid === uuid);
      if (!tpl) {
        console.warn('[CreativeEditor] Template not found for uuid:', uuid);
        setComponents({});
        setFormValues({});
        setHiddenValues({});
        setAllFieldsHidden(false);
        return;
      }

      const ml = metaListRef.current || metaList;
      const textfield = tpl.template_textfield || '';
      const fieldConfig = tpl.field_config || {}; // from Admin settings

      // AS-050 / AS-171: parse variants for this template; auto-select default (new-ad mode only)
      const variants = parseVariants(fieldConfig);
      setParsedVariants(variants);
      // Determine which variant to apply.
      // Priority (new-ad): explicit key override (user changed dropdown) > default variant key.
      // In edit mode, selectedVariantKey is already set from ad.variant_key (or '' for legacy).
      // We only update state in new-ad mode here; edit mode is read-only for variant selection.
      let activeVariantKey = variantKeyOverride;
      if (!editUUID) {
        if (!activeVariantKey) {
          activeVariantKey = getDefaultVariant(variants).variant_key;
        }
        setSelectedVariantKey(activeVariantKey);
      }

      console.log('[CreativeEditor] handleTemplateChange: uuid =', uuid,
        ', template_textfield length =', textfield.length,
        ', has meta_list =', !!ml);

      if (!textfield) {
        console.warn('[CreativeEditor] No template_textfield for template:', tpl.name);
        console.log('[CreativeEditor] Template object keys:', Object.keys(tpl));
        setComponents({});
        setFormValues({});
        setHiddenValues({});
        setAllFieldsHidden(false);
        return;
      }

      const config = parseTemplateTextfield(textfield);

      if (!config) {
        console.error('[CreativeEditor] parseTemplateTextfield returned null');
        setComponents({});
        setFormValues({});
        setHiddenValues({});
        setAllFieldsHidden(false);
        return;
      }

      if (!config.template) {
        console.error('[CreativeEditor] Parse result has no config.template');
        console.error('[CreativeEditor] config keys:', Object.keys(config));
        setComponents({});
        setFormValues({});
        setHiddenValues({});
        setAllFieldsHidden(false);
        return;
      }

      // AS-220: config.template parsed successfully; count entries to detect "all-hidden" vs true parse failure.
      const templateEntryCount = Object.keys(config.template).length;

      // Store raw textfield for save (preserves arrow functions)
      setRawTemplateText(textfield);

      if (!ml) {
        console.error('[CreativeEditor] meta_list not loaded!');
        setComponents({});
        setFormValues({});
        setHiddenValues({});
        setAllFieldsHidden(false);
        return;
      }
      
      console.log('[CreativeEditor] meta_list keys:', Object.keys(ml).slice(0, 10), '...');

      const newComponents = {};
      const newValues = {};
      const newHidden = {};

      // Components are defined in config.template
      // Each entry: { componentName: "VM5xxx", template_overrides: {...}, ... }
      for (const [compKey, compDef] of Object.entries(config.template)) {
        const componentName = compDef.componentName || '';
        const templateOverrides = compDef.template_overrides || {};

        // Look up component in meta_list by componentName
        const metaDef = ml[componentName];
        if (!metaDef) {
          console.log('[CreativeEditor] Component not in meta_list (skipping):', componentName, '(key:', compKey, ')');
          continue;
        }

        // Check if metaDef has any properties (VM5Preload is empty: {})
        const metaKeys = Object.keys(metaDef);
        if (metaKeys.length === 0) {
          // Component has no properties in meta_list, but may have template_overrides
          // Check if there are hidden values to store
          const compHidden = {};
          let hasHiddenValues = false;
          
          for (const [propKey, override] of Object.entries(templateOverrides)) {
            if (override.display === 'hidden' || override.value !== undefined) {
              compHidden[propKey] = override.value !== undefined ? override.value : '';
              hasHiddenValues = true;
            }
          }
          
          if (hasHiddenValues) {
            console.log('[CreativeEditor] Component has no properties but has hidden values:', componentName, Object.keys(compHidden));
            newHidden[compKey] = compHidden;
          } else {
            console.log('[CreativeEditor] Component has no properties and no hidden values (skipping):', componentName);
          }
          continue;
        }

        console.log('[CreativeEditor] Processing component:', compKey, '→', componentName,
          ', props:', metaKeys, ', overrides:', Object.keys(templateOverrides));

        const properties = [];
        const compValues = {};
        const compHidden = {};

        // meta_list structure: { VM5Component: { propKey: { type, format, ... }, ... } }
        for (const [propKey, propDef] of Object.entries(metaDef)) {
          const override = templateOverrides[propKey] || {};

          // AS-089: VM5CloseButton.click_url is always hidden — auto-synced at save time
          if (componentName === 'VM5CloseButton' && propKey === CLICK_URL_KEY) {
            const existingVal = override.value !== undefined ? override.value : propDef.default;
            compHidden[propKey] = existingVal !== undefined ? existingVal : '0';
            console.log('[CreativeEditor]   Hidden field (VM5CloseButton click_url):', compHidden[propKey]);
            continue;
          }

          // Hidden fields: store value but don't render
          // Priority: template_overrides (RD locked) > Admin field_config > default
          if (override.display === 'hidden') {
            compHidden[propKey] = override.value !== undefined ? override.value : propDef.default;
            console.log('[CreativeEditor]   Hidden field (RD locked):', propKey, '=', compHidden[propKey]);
            continue;
          }

          // Admin field_config: check visibility
          const fc = fieldConfig[`${compKey}.${propKey}`];
          if (fc && fc.visible === false) {
            compHidden[propKey] = fc.hidden_value !== undefined && fc.hidden_value !== ''
              ? coerceValue(fc.hidden_value, propDef)
              : propDef.default;
            console.log('[CreativeEditor]   Hidden field (Admin):', propKey, '=', compHidden[propKey]);
            continue;
          }

          const prop = {
            key: propKey,
            label: propDef.label || propKey,
            description: propDef.description || '',
            type: (fc?.type_override) || propDef.type || 'string',
            format: propDef.format || 'string',
            category: fc?.category || propDef.category || '',
            default: override.value !== undefined ? override.value : propDef.default,
            min: propDef.min,
            max: propDef.max,
            options: propDef.options,
            columns: propDef.column,
            columnSize: propDef.column_size,
            accepted: propDef.accepted || [],
            required: !propDef.optional,
            optional: propDef.optional || false,
          };
          properties.push(prop);

          // Set initial value — for image/video, replace fakeimg/placeholder defaults
          let initVal = prop.default !== undefined ? prop.default : getTypeDefault(prop.type, prop.format);
          if (prop.type === 'image' || prop.type === 'video') {
            const isPlaceholderDefault = !initVal || /fakeimg\.pl|placehold|placeholder/i.test(String(initVal));
            if (isPlaceholderDefault) {
              const fcKey = `${compKey}.${propKey}`;
              const fcEntry = fieldConfig[fcKey];
              if (fcEntry?.placeholder_size) {
                initVal = `https://placehold.co/${fcEntry.placeholder_size}?text=${fcEntry.placeholder_size}`;
              } else {
                const defUrl = String(propDef.default || '');
                const m = defUrl.match(/(\d+)x(\d+)/);
                if (m) initVal = `https://placehold.co/${m[1]}x${m[2]}?text=${m[1]}x${m[2]}`;
              }
            }
          }
          // For medias type with a minimum item count, pre-fill with min empty items
          // so sendPreview sends a complete config on the very first call (no render-time dependency).
          // Only applies when initVal is [] (fresh template/variant — not editing existing ad data).
          if (prop.type === 'medias' && prop.min && Array.isArray(initVal) && initVal.length === 0) {
            const mediaCols = parseColumns(prop.columns, [
              { key: 'image', label: '圖片網址', type: 'image' },
              { key: CLICK_URL_KEY, label: '點擊連結', type: 'string' },
            ]);
            // Build placeholder URL inline (mirrors getPlaceholderUrl logic using already-resolved tpl/fieldConfig)
            const fcKey = `${compKey}.${propKey}`;
            const fcEntry = fieldConfig[fcKey];
            let mediaPh = null;
            if (fcEntry?.placeholder_size) {
              mediaPh = `https://placehold.co/${fcEntry.placeholder_size}?text=${fcEntry.placeholder_size}`;
            } else {
              const defUrl = String(propDef.default || '');
              const m = defUrl.match(/(\d+)x(\d+)/);
              if (m) mediaPh = `https://placehold.co/${m[1]}x${m[2]}?text=${m[1]}x${m[2]}`;
            }
            const buildItem = () => {
              const item = {};
              for (const col of mediaCols) {
                let val = col.default || '';
                if (col.type === 'image' && (!val || /fakeimg\.pl|placehold|placeholder/i.test(val))) {
                  if (mediaPh) val = mediaPh;
                }
                item[col.key] = val;
              }
              return item;
            };
            initVal = Array.from({ length: prop.min }, buildItem);
          }
          compValues[propKey] = initVal;
        }

        if (properties.length > 0) {
          newComponents[compKey] = { componentName, properties };
          newValues[compKey] = compValues;
          if (Object.keys(compHidden).length > 0) {
            newHidden[compKey] = compHidden;
          }
        } else if (Object.keys(compHidden).length > 0) {
          // Component has only hidden fields
          newHidden[compKey] = compHidden;
        }
      }

      console.log('[CreativeEditor] Result: components =', Object.keys(newComponents),
        ', formValues keys =', Object.keys(newValues),
        ', hiddenValues keys =', Object.keys(newHidden));

      const hasZeroVisible = Object.keys(newComponents).length === 0 && Object.keys(newValues).length === 0;
      if (hasZeroVisible) {
        console.warn('[CreativeEditor] ⚠️ No components parsed! This will show raw textarea fallback.');
        console.log('[CreativeEditor] Debug info:');
        console.log('  - config.template entries:', Object.entries(config.template).map(([k, v]) => ({
          key: k,
          componentName: v.componentName,
          hasOverrides: !!v.template_overrides && Object.keys(v.template_overrides).length > 0
        })));
      }

      // AS-220: set allFieldsHidden when parse succeeded (templateEntryCount > 0) but 0 visible components.
      // This is distinct from true parse failure (config null / no config.template).
      setAllFieldsHidden(hasZeroVisible && templateEntryCount > 0);

      setComponents(newComponents);

      // AS-050 / AS-171: apply selected variant defaults + locked_fields.
      // New-ad mode: overlay defaults then lock fields.
      // Edit mode: only run locked_fields migration (preserve ad's actual values); skip defaults overlay.
      if (!editUUID) {
        // AS-171: look up by variant_key (stable identity), fall back to default variant.
        const defVariant = variants.find((v) => v.variant_key === activeVariantKey) || getDefaultVariant(variants);
        // defaults keys are composite "compKey:propKey". Only apply to the matching component.
        for (const [compositeKey, defVal] of Object.entries(defVariant.defaults || {})) {
          // Split on the FIRST colon only — component_key must not contain colons
          // (PAC component key is a slug, e.g. "video", "medias", "VM5CloseButton").
          // property_key may contain colons and will be preserved intact in propKey.
          const colonIdx = compositeKey.indexOf(':');
          if (colonIdx === -1) continue; // skip malformed keys
          const compKey = compositeKey.slice(0, colonIdx);
          const propKey = compositeKey.slice(colonIdx + 1);
          if (!(compKey in newValues)) continue; // component not in this template, skip safely
          if (propKey in (newValues[compKey] || {})) {
            const propDef = (newComponents[compKey]?.properties || []).find((p) => p.key === propKey);
            newValues[compKey][propKey] = propDef ? coerceValue(defVal, propDef) : defVal;
          }
        }
        // locked_fields: composite "compKey:propKey". Only lock the matching component's field.
        // Use the variant's default as the locked value so the correct value is still sent to PAC.
        for (const compositeKey of (defVariant.locked_fields || [])) {
          const colonIdx = compositeKey.indexOf(':');
          if (colonIdx === -1) continue; // skip malformed keys
          const compKey = compositeKey.slice(0, colonIdx);
          const propKey = compositeKey.slice(colonIdx + 1);
          if (!(compKey in newComponents)) continue; // component not in this template, skip safely
          if (propKey in (newValues[compKey] || {})) {
            const variantDefault = defVariant.defaults?.[compositeKey];
            const isUsableDefault =
              variantDefault !== undefined &&
              variantDefault !== null &&
              String(variantDefault).trim() !== '' &&
              !/placehold|fakeimg/i.test(String(variantDefault));
            const lockedVal = isUsableDefault ? variantDefault : newValues[compKey][propKey];
            // Move to hidden
            if (!newHidden[compKey]) newHidden[compKey] = {};
            newHidden[compKey][propKey] = lockedVal;
            delete newValues[compKey][propKey];
            // Remove from components.properties so it is not rendered
            if (newComponents[compKey]) {
              newComponents[compKey] = {
                ...newComponents[compKey],
                properties: newComponents[compKey].properties.filter((p) => p.key !== propKey),
              };
            }
          }
        }
      } else {
        // AS-171 edit mode: apply locked_fields migration only (no defaults overlay).
        // Look up the variant by variant_key (stable identity); skip if '' or not found (legacy ad).
        // selectedVariantKey is set from ad.variant_key during init (or '' for legacy/null).
        if (selectedVariantKey) {
          const adVariant = variants.find((v) => v.variant_key === selectedVariantKey);
          if (adVariant) {
            // locked_fields: composite "compKey:propKey". Only lock the matching component's field.
            // The value stays as-is (from the ad's saved data, not the variant default).
            for (const compositeKey of (adVariant.locked_fields || [])) {
              // Split on the FIRST colon only — component_key must not contain colons
              // (PAC component key is a slug, e.g. "video", "medias", "VM5CloseButton").
              // property_key may contain colons and will be preserved intact in propKey.
              const colonIdx = compositeKey.indexOf(':');
              if (colonIdx === -1) continue; // skip malformed keys
              const compKey = compositeKey.slice(0, colonIdx);
              const propKey = compositeKey.slice(colonIdx + 1);
              if (!(compKey in newComponents)) continue; // component not in this template, skip safely
              if (propKey in (newValues[compKey] || {})) {
                const existingVal = newValues[compKey][propKey];
                if (!newHidden[compKey]) newHidden[compKey] = {};
                newHidden[compKey][propKey] = existingVal;
                delete newValues[compKey][propKey];
                if (newComponents[compKey]) {
                  newComponents[compKey] = {
                    ...newComponents[compKey],
                    properties: newComponents[compKey].properties.filter((p) => p.key !== propKey),
                  };
                }
              }
            }
          }
        }
      }

      // If we have pending creative values (from loading existing creative), overlay them
      if (pendingCreativeValuesRef.current) {
        const existing = pendingCreativeValuesRef.current;
        pendingCreativeValuesRef.current = null;
        console.log('[CreativeEditor] Applying existing creative values:', Object.keys(existing));
        for (const [compKey, vals] of Object.entries(existing)) {
          if (newValues[compKey] && typeof vals === 'object' && !Array.isArray(vals)) {
            const propDefs = (newComponents[compKey]?.properties || []).reduce((acc, p) => {
              acc[p.key] = p;
              return acc;
            }, {});
            const coerced = {};
            for (const [k, v] of Object.entries(vals)) {
              coerced[k] = propDefs[k] ? coerceValue(v, propDefs[k]) : v;
            }
            newValues[compKey] = { ...newValues[compKey], ...coerced };
          }
        }
      }

      setFormValues(newValues);
      setHiddenValues(newHidden);
    },
    [templates, metaList]
  );

  // AS-171: when user picks a different variant, re-parse the template with the new variant_key applied.
  const handleVariantChange = useCallback(
    (variantKey) => {
      if (selectedTemplateUUID) {
        handleTemplateChange(selectedTemplateUUID, variantKey);
      }
    },
    [selectedTemplateUUID, handleTemplateChange]
  );

  // Auto-trigger handleTemplateChange when template is pre-selected from init
  useEffect(() => {
    if (selectedTemplateUUID && templates.length > 0) {
      // 新建模式：components 為空時觸發
      // 編輯模式：pendingCreativeValuesRef 有值且尚未完成 edit init 時強制觸發
      if (Object.keys(components).length === 0 || (pendingCreativeValuesRef.current && !editInitDoneRef.current)) {
        editInitDoneRef.current = true;
        handleTemplateChange(selectedTemplateUUID);
      }
    }
  }, [selectedTemplateUUID, templates, metaList]); // eslint-disable-line

  // Get placeholder URL for image/video fields
  function getPlaceholderUrl(compKey, propKey, metaDef) {
    const tpl = templates.find((t) => t.uuid === selectedTemplateUUID);
    const fc = tpl?.field_config?.[`${compKey}.${propKey}`];
    // 1. Admin setting
    if (fc?.placeholder_size) {
      return `https://placehold.co/${fc.placeholder_size}?text=${fc.placeholder_size}`;
    }
    // 2. Parse from meta_list default URL
    const defaultUrl = metaDef?.default || '';
    const sizeMatch = String(defaultUrl).match(/(\d+)x(\d+)/);
    if (sizeMatch) {
      return `https://placehold.co/${sizeMatch[1]}x${sizeMatch[2]}?text=${sizeMatch[1]}x${sizeMatch[2]}`;
    }
    return null;
  }

  function getTypeDefault(type, format) {
    switch (type) {
      case 'number':
        return 0;
      case 'boolean':
        return false;
      case 'medias':
        return [];
      case 'form':
        return [];
      case 'image':
      case 'video':
      case 'string':
        // string with format 'array' defaults to empty array
        if (format === 'array') return [];
        return '';
      default:
        return '';
    }
  }

  const updateValue = (compKey, propKey, value) => {
    setFormValues((prev) => ({
      ...prev,
      [compKey]: { ...prev[compKey], [propKey]: value },
    }));
  };

  const toggleSection = (section) => {
    setOpenSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  // --- Preview ---
  const previewIframeRef = useRef(null);
  const previewContainerRef = useRef(null);
  const previewTimerRef = useRef(null);
  const PREVIEW_URL = CDN.PREVIEW;
  const IFRAME_W = 800;
  const IFRAME_H = 850;

  // Scale iframe to fit container
  useEffect(() => {
    const container = previewContainerRef.current;
    if (!container) return;
    const updateScale = () => {
      const cw = container.clientWidth;
      const ch = container.clientHeight;
      const scale = Math.min(cw / IFRAME_W, ch / IFRAME_H) * 0.85;
      container.style.setProperty('--preview-scale', scale);
      container.style.setProperty('--preview-w', IFRAME_W + 'px');
      container.style.setProperty('--preview-h', IFRAME_H + 'px');
    };
    updateScale();
    window.addEventListener('resize', updateScale);
    return () => window.removeEventListener('resize', updateScale);
  }, [selectedTemplateUUID, rawTemplateText]);
  const PREVIEW_ORIGIN = CDN.PREVIEW_ORIGIN;

  // AS-089: Helper — compute hiddenValues with VM5CloseButton click_url resolved.
  // Scans in components[] order (= template schema order) to guarantee stable priority:
  //   1. Top-level click_url on each component (by properties[] order)
  //   2. medias cuts click_url (by component order, then cut index)
  // Returns a new object; never mutates the originals.
  const computeResolvedHiddenValues = (fv, comps, hv) => {
    const closeButtonCompKey = Object.keys(comps).find(
      (k) => comps[k]?.componentName === 'VM5CloseButton'
    );
    if (!closeButtonCompKey) return hv;

    let firstClickUrl = null;

    // 1. Top-level click_url — iterate in components[] order, then properties[] order
    outer1: for (const [compKey, compDef] of Object.entries(comps)) {
      if (compKey === closeButtonCompKey) continue; // skip self
      const vals = fv[compKey];
      if (!vals) continue;
      for (const prop of (compDef.properties || [])) {
        if (prop.key !== CLICK_URL_KEY) continue;
        const v = (vals[CLICK_URL_KEY] || '').trim();
        if (v && v !== '0') { firstClickUrl = v; break outer1; }
      }
    }

    // 2. medias cuts click_url — same component order
    if (!firstClickUrl) {
      outer2: for (const [compKey, compDef] of Object.entries(comps)) {
        if (compKey === closeButtonCompKey) continue;
        const vals = fv[compKey];
        if (!vals) continue;
        for (const prop of (compDef.properties || [])) {
          if ((prop.type || '').toLowerCase() !== 'medias') continue;
          const cuts = vals[prop.key];
          if (!Array.isArray(cuts)) continue;
          for (const cut of cuts) {
            const v = ((cut?.[CLICK_URL_KEY]) || '').trim();
            if (v && v !== '0') { firstClickUrl = v; break outer2; }
          }
        }
      }
    }

    const syncedUrl = firstClickUrl || '0';
    console.log('[CreativeEditor] AS-089: syncing VM5CloseButton click_url →', syncedUrl);
    return {
      ...hv,
      [closeButtonCompKey]: {
        ...(hv[closeButtonCompKey] || {}),
        [CLICK_URL_KEY]: syncedUrl,
      },
    };
  };

  const sendPreview = useCallback(() => {
    const iframe = previewIframeRef.current;
    if (!iframe || !iframe.contentWindow || !rawTemplateText) return;

    const properties = {};
    for (const [compKey, vals] of Object.entries(formValues)) {
      if (compKey === '_raw') continue;
      properties[compKey] = { ...vals };
    }

    // AS-089: Sync VM5CloseButton click_url before preview (same logic as handleSave)
    const previewHidden = computeResolvedHiddenValues(formValues, components, hiddenValues);

    for (const [compKey, vals] of Object.entries(previewHidden)) {
      properties[compKey] = { ...(properties[compKey] || {}), ...vals };
    }

    // Build preview config: arrow functions as STRINGS + context on each component
    const previewConfig = buildPreviewConfig(properties, rawTemplateText);
    iframe.contentWindow.postMessage(
      { creativeConfig: JSON.stringify(previewConfig) },
      PREVIEW_ORIGIN
    );
  }, [formValues, hiddenValues, rawTemplateText, components]);

  // Debounced preview update on form change
  useEffect(() => {
    if (!rawTemplateText) return;
    if (previewTimerRef.current) clearTimeout(previewTimerRef.current);
    previewTimerRef.current = setTimeout(sendPreview, 500);
    return () => clearTimeout(previewTimerRef.current);
  }, [formValues, hiddenValues, sendPreview, rawTemplateText]);

  // --- Upload ---
  // colKey is the actual column key in the media item object (e.g. 'video', 'src', 'img_url').
  // For single-image fields (renderUploadableField) colKey is not passed and falls back to 'image'.
  const handleFileUpload = async (file, compKey, propKey, mediaIdx, mediaType, colKey) => {
    const isImage = /\.(png|jpe?g|gif)$/i.test(file.name);
    const isVideo = /\.(mp4|webm)$/i.test(file.name);
    const maxSize = isVideo ? 50 * 1024 * 1024 : 5 * 1024 * 1024;

    if (!isImage && !isVideo) {
      addToast('不支援的檔案格式', 'error');
      return;
    }
    if (mediaType === 'video' && !isVideo) {
      addToast('此欄位僅支援影片格式（mp4, webm）', 'error');
      return;
    }
    if (mediaType === 'image' && !isImage) {
      addToast('此欄位僅支援圖片格式（png, jpg, gif）', 'error');
      return;
    }
    if (file.size > maxSize) {
      addToast(`檔案大小超過上限（${isVideo ? '50MB' : '5MB'}）`, 'error');
      return;
    }

    // For single-image fields, loading state is managed here.
    // For medias fields, loading state is managed by the caller (renderMediasField) via .finally().
    if (mediaIdx === undefined) {
      const fieldKey = `${compKey}-${propKey}`;
      setUploadingFields((prev) => ({ ...prev, [fieldKey]: 'uploading' }));
      try {
        const result = await api.uploadFile(file);
        updateValue(compKey, propKey, result.resourceUrl);
        addToast('上傳成功', 'success');
      } catch (err) {
        addToast('上傳失敗：' + (err.message || '未知錯誤'), 'error');
      } finally {
        setUploadingFields((prev) => ({ ...prev, [fieldKey]: null }));
      }
    } else {
      try {
        const result = await api.uploadFile(file);
        const cdnUrl = result.resourceUrl;
        const items = [...(formValues[compKey]?.[propKey] || [])];
        items[mediaIdx] = { ...items[mediaIdx], [colKey || 'image']: cdnUrl };
        updateValue(compKey, propKey, items);
        addToast('上傳成功', 'success');
      } catch (err) {
        addToast('上傳失敗：' + (err.message || '未知錯誤'), 'error');
      }
    }
  };

  // Build config_object and save
  const handleSave = async () => {
    if (!selectedTemplateUUID) {
      addToast('請先選擇模板', 'error');
      return;
    }

    setSaving(true);
    try {
      // If using raw JSON fallback mode
      if (formValues._raw) {
        const configObject = formValues._raw;
        console.log('[CreativeEditor] handleSave: using raw JSON');
        if (editUUID) {
          // AS-210: include creative_name if user provided one; otherwise omit (backend keeps existing)
          const updatePayload = {
            template_uuid: selectedTemplateUUID,
            config_object: configObject,
          };
          if (creativeName.trim()) updatePayload.creative_name = creativeName.trim();
          await api.updateCreative(id, editUUID, updatePayload);
          addToast('素材更新成功', 'success');
        } else {
          // AS-210: send creative_name when user filled it; empty string lets backend auto-name
          await api.addCreative(id, {
            template_uuid: selectedTemplateUUID,
            creative_name: creativeName.trim(),
            config_object: configObject,
          });
          // AS-171: persist variant_key + variant_name snapshot on the ad record.
          // Only for templates with real variants (variant_key non-empty).
          // Note: ad is created by the Wizard before reaching the editor, so this
          // cannot be included in CreateAd — a post-create update is required here.
          const activeVariant = parsedVariants.find((v) => v.variant_key === selectedVariantKey);
          if (activeVariant && activeVariant.variant_key) {
            try {
              await api.updateAdVariant(id, activeVariant.variant_key, activeVariant.name);
            } catch (variantErr) {
              console.warn('[CreativeEditor] AS-171: failed to save variant_key (raw):', variantErr);
              addToast('素材已建立，但版本資訊儲存失敗：' + (variantErr.message || '未知錯誤'), 'error');
            }
          }
          addToast('素材建立成功', 'success');
        }
        navigate(`/ads/${id}?tab=creatives`);
        return;
      }

      // AS-089: Compute first non-empty, non-"0" click_url across all components
      // and write it into VM5CloseButton's hiddenValues before building properties.
      const resolvedHiddenValues = computeResolvedHiddenValues(formValues, components, hiddenValues);

      // Build properties: merge visible form values + hidden values
      // Structure: { componentKey: { propKey: value, ... }, ... }
      const properties = {};
      for (const [compKey, vals] of Object.entries(formValues)) {
        properties[compKey] = { ...vals };
      }
      // Merge hidden values (use resolved copy that includes VM5CloseButton sync)
      for (const [compKey, vals] of Object.entries(resolvedHiddenValues)) {
        properties[compKey] = { ...(properties[compKey] || {}), ...vals };
      }

      // Build config_object by combining JSON properties with raw template text
      // The raw template text preserves arrow functions that JSON.stringify would lose
      const configObject = buildConfigObject(properties, rawTemplateText);
      console.log('[CreativeEditor] handleSave: config_object =', configObject.substring(0, 200));

      if (editUUID) {
        // AS-210: include creative_name if user provided one; otherwise omit (backend keeps existing)
        const updatePayload = {
          template_uuid: selectedTemplateUUID,
          config_object: configObject,
        };
        if (creativeName.trim()) updatePayload.creative_name = creativeName.trim();
        await api.updateCreative(id, editUUID, updatePayload);
        addToast('素材更新成功', 'success');
      } else {
        // AS-210: send creative_name when user filled it; empty string lets backend auto-name
        await api.addCreative(id, {
          template_uuid: selectedTemplateUUID,
          creative_name: creativeName.trim(),
          config_object: configObject,
        });
        // AS-171: persist variant_key + variant_name snapshot on the ad record.
        // Only for templates with real variants (variant_key non-empty).
        const activeVariant = parsedVariants.find((v) => v.variant_key === selectedVariantKey);
        if (activeVariant && activeVariant.variant_key) {
          try {
            await api.updateAdVariant(id, activeVariant.variant_key, activeVariant.name);
          } catch (variantErr) {
            console.warn('[CreativeEditor] AS-171: failed to save variant_key:', variantErr);
            addToast('素材已建立，但版本資訊儲存失敗：' + (variantErr.message || '未知錯誤'), 'error');
          }
        }
        addToast('素材建立成功', 'success');
      }
      navigate(`/ads/${id}?tab=creatives`);
    } catch (err) {
      // AS-228: don't prefix — backend message (incl. AS-212 sync-fail case for
      // create/update creative) is already a complete, unambiguous sentence.
      addToast(err.message || '儲存素材失敗：未知錯誤', 'error');
    } finally {
      setSaving(false);
    }
  };

  // --- Render helpers ---

  function renderField(compKey, prop) {
    const value = formValues[compKey]?.[prop.key];
    const acceptedHint = prop.accepted && prop.accepted.length > 0
      ? prop.accepted.join(', ')
      : '';

    switch (prop.type) {
      case 'image':
        return renderUploadableField(compKey, prop, value, 'image', acceptedHint);

      case 'video':
        return renderUploadableField(compKey, prop, value, 'video', acceptedHint);

      case 'number':
        return (
          <div className="creative-field" key={`${compKey}-${prop.key}`}>
            <label className="creative-field-label">
              {prop.description || prop.label}
              {prop.min !== undefined && prop.max !== undefined && (
                <span className="creative-field-hint">
                  ({prop.min} ~ {prop.max})
                </span>
              )}
            </label>
            <input
              type="number"
              value={value ?? ''}
              min={prop.min}
              max={prop.max}
              onChange={(e) => updateValue(compKey, prop.key, Number(e.target.value))}
            />
          </div>
        );

      case 'boolean':
        return (
          <div className="creative-field" key={`${compKey}-${prop.key}`}>
            <div className="creative-field-checkbox">
              <input
                type="checkbox"
                id={`${compKey}-${prop.key}`}
                checked={!!value}
                onChange={(e) => updateValue(compKey, prop.key, e.target.checked)}
              />
              <label htmlFor={`${compKey}-${prop.key}`}>
                {prop.description || prop.label}
              </label>
            </div>
          </div>
        );

      case 'color':
        return (
          <div className="creative-field" key={`${compKey}-${prop.key}`}>
            <label className="creative-field-label">
              {prop.description || prop.label}
            </label>
            <div className="color-field-row">
              <input
                type="text"
                value={value || ''}
                onChange={(e) => updateValue(compKey, prop.key, e.target.value)}
                placeholder="#000000"
              />
              <div
                className="color-swatch"
                style={{ background: value || '#CCCCCC' }}
                onClick={() => {
                  const input = document.createElement('input');
                  input.type = 'color';
                  input.value = value || '#000000';
                  input.addEventListener('input', (e) => updateValue(compKey, prop.key, e.target.value));
                  input.click();
                }}
              />
            </div>
          </div>
        );

      case 'select':
        return (
          <div className="creative-field" key={`${compKey}-${prop.key}`}>
            <label className="creative-field-label">
              {prop.description || prop.label}
            </label>
            <select
              value={value || ''}
              onChange={(e) => updateValue(compKey, prop.key, e.target.value)}
            >
              <option value="">請選擇</option>
              {(prop.options || []).map((opt) => {
                const optVal = typeof opt === 'object' ? opt.value : opt;
                const optLabel = typeof opt === 'object' ? opt.label : opt;
                return (
                  <option key={optVal} value={optVal}>
                    {optLabel}
                  </option>
                );
              })}
            </select>
          </div>
        );

      case 'medias':
        return (
          <div className="creative-field" key={`${compKey}-${prop.key}`}>
            <label className="creative-field-label">
              {prop.description || prop.label}
              {(prop.min !== undefined || prop.max !== undefined) && (
                <span className="creative-field-hint">
                  {prop.min !== undefined && prop.max !== undefined
                    ? `${prop.min} ~ ${prop.max} 張`
                    : prop.min !== undefined
                      ? `至少 ${prop.min} 張`
                      : `最多 ${prop.max} 張`}
                </span>
              )}
            </label>
            {renderMediasField(compKey, prop)}
          </div>
        );

      case 'form':
        return (
          <div className="creative-field" key={`${compKey}-${prop.key}`}>
            <label className="creative-field-label">
              {prop.description || prop.label}
            </label>
            {renderFormField(compKey, prop)}
          </div>
        );

      case 'string':
      default:
        // Handle string with format: 'array' (e.g. pixel IDs)
        if (prop.format === 'array') {
          return renderStringArrayField(compKey, prop);
        }
        return (
          <div className="creative-field" key={`${compKey}-${prop.key}`}>
            <label className="creative-field-label">
              {prop.description || prop.label}
              {prop.optional && (
                <span className="creative-field-hint">（選填）</span>
              )}
            </label>
            <input
              type="text"
              value={value || ''}
              onChange={(e) => updateValue(compKey, prop.key, e.target.value)}
            />
          </div>
        );
    }
  }

  // Render image/video field with drag-drop upload + URL input
  function renderUploadableField(compKey, prop, value, mediaType, acceptedHint) {
    const fieldKey = `${compKey}-${prop.key}`;
    const isUploading = uploadingFields[fieldKey] === 'uploading';
    const acceptExts = mediaType === 'video' ? '.mp4,.webm' : '.png,.jpg,.jpeg,.gif';
    // 只有 alpha video 欄位才顯示 Alpha Converter 入口
    const showAlphaConverter = mediaType === 'video' && prop.key === 'alpha';

    const handleDrop = (e) => {
      e.preventDefault();
      e.currentTarget.classList.remove('upload-zone--dragover');
      const file = e.dataTransfer.files[0];
      if (file) handleFileUpload(file, compKey, prop.key, undefined, mediaType);
    };

    return (
      <div className="creative-field" key={fieldKey}>
        <label className="creative-field-label">
          {prop.description || prop.label}
          {acceptedHint && <span className="creative-field-hint">格式：{acceptedHint}</span>}
          {prop.optional && <span className="creative-field-hint">（選填）</span>}
        </label>
        <div
          className={`upload-zone ${isUploading ? 'upload-zone--uploading' : ''}`}
          onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('upload-zone--dragover'); }}
          onDragLeave={(e) => e.currentTarget.classList.remove('upload-zone--dragover')}
          onDrop={handleDrop}
          onClick={() => !isUploading && document.getElementById(`file-${fieldKey}`)?.click()}
        >
          <input
            id={`file-${fieldKey}`}
            type="file"
            accept={acceptExts}
            style={{ display: 'none' }}
            onChange={(e) => {
              const file = e.target.files[0];
              if (file) handleFileUpload(file, compKey, prop.key, undefined, mediaType);
              e.target.value = '';
            }}
          />
          {isUploading ? (
            <span className="upload-zone__text">上傳中...</span>
          ) : (
            <span className="upload-zone__text">拖拉檔案至此 或 點擊選擇</span>
          )}
        </div>
        {showAlphaConverter && (
          <div className="alpha-converter-entry">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                setAlphaConverterCtx({ compKey, propKey: prop.key });
                setAlphaConverterOpen(true);
              }}
              disabled={isUploading}
            >
              從圖片建立 alpha 素材
            </Button>
          </div>
        )}
        <div className="upload-url-row">
          <span className="upload-url-label">或直接輸入 URL：</span>
          <input
            type="url"
            value={value || ''}
            onChange={(e) => updateValue(compKey, prop.key, e.target.value)}
            placeholder="https://..."
            disabled={isUploading}
          />
        </div>
        {value && mediaType === 'image' && (
          <img src={value} alt="preview" className="image-preview"
            onError={(e) => (e.target.style.display = 'none')}
            onLoad={(e) => (e.target.style.display = 'block')} />
        )}
        {value && mediaType === 'video' && (
          <video
            src={value}
            controls
            preload="metadata"
            className="image-preview"
            onError={(e) => (e.target.style.display = 'none')}
          />
        )}
        {!value && mediaType === 'image' && (() => {
          const phUrl = getPlaceholderUrl(compKey, prop.key, prop);
          return phUrl ? (
            <img src={phUrl} alt="placeholder" className="image-preview placeholder-image" />
          ) : null;
        })()}
      </div>
    );
  }

  // Render a string field with format: 'array' (comma-separated input → array)
  function renderStringArrayField(compKey, prop) {
    const value = formValues[compKey]?.[prop.key];
    const displayVal = Array.isArray(value) ? value.join(', ') : (value || '');

    return (
      <div className="creative-field" key={`${compKey}-${prop.key}`}>
        <label className="creative-field-label">
          {prop.description || prop.label}
          <span className="creative-field-hint">（以逗號分隔多個值）</span>
        </label>
        <input
          type="text"
          value={displayVal}
          onChange={(e) => {
            const raw = e.target.value;
            const arr = raw.split(',').map(s => s.trim()).filter(Boolean);
            updateValue(compKey, prop.key, arr);
          }}
          placeholder="例如: value1, value2"
        />
      </div>
    );
  }

  function renderMediasField(compKey, prop) {
    const items = formValues[compKey]?.[prop.key] || [];

    // meta_list column format: [{ colKey1: { type, description, ... }, colKey2: { ... } }]
    // Each element in the array is an object with one or more column definitions
    const columns = parseColumns(prop.columns, [
      { key: 'image', label: '圖片網址', type: 'image' },
      { key: CLICK_URL_KEY, label: '點擊連結', type: 'string' },
    ]);

    const buildEmptyItem = () => {
      const item = {};
      for (const col of columns) {
        let val = col.default || '';
        // For image columns, use placeholder if default is empty or a placeholder URL
        if (col.type === 'image' && (!val || /fakeimg\.pl|placehold|placeholder/i.test(val))) {
          const phUrl = getPlaceholderUrl(compKey, prop.key, prop);
          if (phUrl) val = phUrl;
        }
        item[col.key] = val;
      }
      return item;
    };

    const addItem = () => {
      if (prop.max && items.length >= prop.max) return;
      updateValue(compKey, prop.key, [...items, buildEmptyItem()]);
    };

    const removeItem = (idx) => {
      if (prop.min && items.length <= prop.min) return;
      updateValue(compKey, prop.key, items.filter((_, i) => i !== idx));
    };

    const updateItem = (idx, field, val) => {
      const newItems = [...items];
      newItems[idx] = { ...newItems[idx], [field]: val };
      updateValue(compKey, prop.key, newItems);
    };

    const canRemove = !prop.min || items.length > prop.min;
    const canAdd = !prop.max || items.length < prop.max;

    return (
      <div className="repeatable-group">
        {items.map((item, idx) => (
          <div className="repeatable-item" key={idx}>
            <div className="repeatable-item-fields">
              <div className="repeatable-item-number">{idx + 1}</div>
              <div className="repeatable-item-columns">
                {columns.filter((col) => col.key !== CLICK_URL_KEY).map((col) => {
                  const colType = (col.type || '').toLowerCase();
                  const isImage = colType === 'image' || col.key.toLowerCase().includes('image');
                  const colAccepted = col.accepted && col.accepted.length > 0
                    ? col.accepted.join(', ') : '';
                  const mediaFieldKey = `${compKey}-${prop.key}-${idx}-${col.key}`;
                  const isMediaUploading = uploadingFields[mediaFieldKey] === 'uploading';
                  return (
                    <div className="repeatable-column" key={col.key}>
                      <label className="repeatable-column-label">
                        {col.label || col.key}
                        {colAccepted && (
                          <span className="creative-field-hint"> ({colAccepted})</span>
                        )}
                      </label>
                      {isImage ? (
                        <>
                          <div
                            className={`upload-zone upload-zone--sm ${isMediaUploading ? 'upload-zone--uploading' : ''}`}
                            onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('upload-zone--dragover'); }}
                            onDragLeave={(e) => e.currentTarget.classList.remove('upload-zone--dragover')}
                            onDrop={(e) => {
                              e.preventDefault();
                              e.currentTarget.classList.remove('upload-zone--dragover');
                              const file = e.dataTransfer.files[0];
                              if (file) {
                                setUploadingFields((prev) => ({ ...prev, [mediaFieldKey]: 'uploading' }));
                                handleFileUpload(file, compKey, prop.key, idx, 'image', col.key).finally(() => {
                                  setUploadingFields((prev) => ({ ...prev, [mediaFieldKey]: null }));
                                });
                              }
                            }}
                            onClick={() => !isMediaUploading && document.getElementById(`file-${mediaFieldKey}`)?.click()}
                          >
                            <input
                              id={`file-${mediaFieldKey}`}
                              type="file"
                              accept=".png,.jpg,.jpeg,.gif"
                              style={{ display: 'none' }}
                              onChange={(e) => {
                                const file = e.target.files[0];
                                if (file) {
                                  setUploadingFields((prev) => ({ ...prev, [mediaFieldKey]: 'uploading' }));
                                  handleFileUpload(file, compKey, prop.key, idx, 'image', col.key).finally(() => {
                                    setUploadingFields((prev) => ({ ...prev, [mediaFieldKey]: null }));
                                  });
                                }
                                e.target.value = '';
                              }}
                            />
                            {isMediaUploading ? '上傳中...' : '拖拉或點擊上傳'}
                          </div>
                          <div className="upload-url-row">
                            <span className="upload-url-label">或輸入 URL：</span>
                            <input
                              type="url"
                              placeholder={col.label || col.key}
                              value={item[col.key] || ''}
                              onChange={(e) => updateItem(idx, col.key, e.target.value)}
                              disabled={isMediaUploading}
                            />
                          </div>
                          {item[col.key] ? (
                            <img
                              src={item[col.key]}
                              alt="preview"
                              className="image-preview-small"
                              onError={(e) => (e.target.style.display = 'none')}
                              onLoad={(e) => (e.target.style.display = 'block')}
                            />
                          ) : (() => {
                            const phUrl = getPlaceholderUrl(compKey, prop.key, prop);
                            return phUrl ? (
                              <img src={phUrl} alt="placeholder" className="image-preview-small placeholder-image" />
                            ) : null;
                          })()}
                        </>
                      ) : (
                        <input
                          type="url"
                          placeholder={col.label || col.key}
                          value={item[col.key] || ''}
                          onChange={(e) => updateItem(idx, col.key, e.target.value)}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            <button
              className="btn-remove-item"
              onClick={() => removeItem(idx)}
              disabled={!canRemove}
              title={!canRemove ? `至少需要 ${prop.min} 項` : '刪除'}
            >
              <Icon name="x" size={14} />
            </button>
          </div>
        ))}
        <button
          className="btn-add-item"
          onClick={addItem}
          disabled={!canAdd}
        >
          + 新增素材{!canAdd ? ` (已達上限 ${prop.max})` : ''}
        </button>
      </div>
    );
  }

  // ─── Tracking helpers ───

  function detectTracking(input) {
    const str = input || '';
    let party = 'custom';
    if (str.includes('doubleclick.net')) party = 'DCM';
    else if (str.includes('adsafeprotected.com')) party = 'IAS';
    else if (str.includes('doubleverify.com')) party = 'DoubleVerify';
    else if (str.includes('lndata')) party = 'LnData';
    else if (str.includes('nielsen') || str.includes('imrworldwide')) party = 'Dar';

    const event = 'impression';

    let tagType = 'Tag';
    const hasScript = /<SCRIPT|<script/.test(str);
    const hasNoscript = /<NOSCRIPT|<noscript/.test(str);
    const hasImg = /<IMG|<img/.test(str);
    const hasIframe = /<IFRAME|<iframe/.test(str);
    if (hasScript && (hasNoscript || hasImg)) tagType = 'Mixed Tag';
    else if (hasScript) tagType = 'JS Tag';
    else if (hasImg) tagType = 'Image Tag';
    else if (hasIframe) tagType = 'iFrame Tag';
    else if (str.trim().startsWith('http') && !str.includes('<')) tagType = 'URL';

    return { party, event, tagType };
  }

  function stripMacros(str) {
    return str
      .replace(/\$\{[^}]*\}/g, '')   // Remove ${...} macros
      .replace(/\s+/g, ' ')           // Collapse whitespace/newlines to single space
      .trim();
  }

  function renderTrackingField(compKey, prop) {
    const items = formValues[compKey]?.[prop.key] || [];

    const openAddModal = () => {
      setTrackingModalCtx({ compKey, prop });
      setTrackingInput('');
      setTrackingParty('custom');
      setTrackingEvent('impression');
      setTrackingEditIdx(null);
      setTrackingModalOpen(true);
    };

    const openEditModal = (idx) => {
      const item = items[idx];
      setTrackingModalCtx({ compKey, prop });
      setTrackingInput(item.tag_content || '');
      setTrackingParty(item.party || 'custom');
      setTrackingEvent(item.event || 'impression');
      setTrackingEditIdx(idx);
      setTrackingModalOpen(true);
    };

    const handleConfirmAdd = () => {
      const cleaned = stripMacros(trackingInput.trim());
      const newItem = {
        party: trackingParty,
        event: trackingEvent,
        tag_content: cleaned,
      };
      if (trackingEditIdx !== null) {
        const updated = [...items];
        updated[trackingEditIdx] = newItem;
        updateValue(compKey, prop.key, updated);
      } else {
        updateValue(compKey, prop.key, [...items, newItem]);
      }
      setTrackingModalOpen(false);
      setTrackingInput('');
      setTrackingEditIdx(null);
    };

    const handleDelete = (idx) => {
      updateValue(compKey, prop.key, items.filter((_, i) => i !== idx));
    };

    const detectedInModal = detectTracking(trackingInput);

    return (
      <div className="tracking-list">
        {items.length === 0 ? (
          <div className="tracking-empty">尚未新增追蹤碼</div>
        ) : (
          items.map((item, idx) => {
            const det = detectTracking(item.tag_content || '');
            const isKnown = item.party && item.party !== 'custom';
            const preview = (item.tag_content || '').length > 60
              ? (item.tag_content || '').substring(0, 60) + '...'
              : (item.tag_content || '');
            return (
              <div className="tracking-item" key={idx}>
                <span className={`tracking-badge ${isKnown ? 'tracking-badge--party' : 'tracking-badge--party-custom'}`}>{item.party || 'custom'}</span>
                <span className="tracking-badge tracking-badge--event">{item.event || 'impression'}</span>
                <span className="tracking-badge tracking-badge--type">{det.tagType}</span>
                <span className="tracking-preview">{preview}</span>
                <Button variant="ghost" size="sm" className="tracking-view-link" onClick={() => setTrackingViewTarget({ item, detected: det })}>查看</Button>
                <Button variant="ghost" size="sm" className="tracking-view-link" onClick={() => openEditModal(idx)}>編輯</Button>
                <Button variant="ghost" size="sm" className="btn-remove-item" onClick={() => handleDelete(idx)} title="刪除">
                  <Icon name="x" size={14} />
                </Button>
              </div>
            );
          })
        )}
        <Button variant="ghost" size="sm" className="btn-add-item" onClick={openAddModal}>+ 新增追蹤碼</Button>

        {/* Add Modal */}
        {trackingModalOpen && trackingModalCtx?.compKey === compKey && trackingModalCtx?.prop?.key === prop.key && (
          <Modal
            title={trackingEditIdx !== null ? '編輯追蹤碼' : '新增追蹤碼'}
            onClose={() => { setTrackingModalOpen(false); setTrackingEditIdx(null); }}
            size="md"
            footer={
              <>
                <Button variant="secondary" onClick={() => { setTrackingModalOpen(false); setTrackingEditIdx(null); }}>取消</Button>
                <Button disabled={!trackingInput.trim()} onClick={handleConfirmAdd}>{trackingEditIdx !== null ? '確認修改' : '確認新增'}</Button>
              </>
            }
          >
            <textarea
              className="tracking-textarea"
              placeholder="貼上追蹤碼 (HTML Tag 或 URL)"
              value={trackingInput}
              onChange={(e) => {
                const val = e.target.value;
                setTrackingInput(val);
                const det = detectTracking(val);
                setTrackingParty(det.party);
                setTrackingEvent(det.event);
              }}
            />
            <div className="tracking-hint">儲存時將自動移除 $&#123;...&#125; 巨集變數</div>
            {trackingInput.trim() && (
              <div className="tracking-detection">
                <span className={`tracking-badge ${detectedInModal.party !== 'custom' ? 'tracking-badge--party' : 'tracking-badge--party-custom'}`}>{detectedInModal.party}</span>
                <span className="tracking-badge tracking-badge--event">{detectedInModal.event}</span>
                <span className="tracking-badge tracking-badge--type">{detectedInModal.tagType}</span>
              </div>
            )}
            <div className="tracking-selects">
              <div className="tracking-select-group">
                <label>廠商</label>
                <select value={trackingParty} onChange={(e) => setTrackingParty(e.target.value)}>
                  {KNOWN_PARTIES.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
              <div className="tracking-select-group">
                <label>事件</label>
                <select value={trackingEvent} onChange={(e) => setTrackingEvent(e.target.value)}>
                  {EVENT_OPTIONS.map((ev) => <option key={ev} value={ev}>{ev}</option>)}
                </select>
              </div>
            </div>
          </Modal>
        )}

        {/* View Modal */}
        {trackingViewTarget && (() => {
          const { item, detected } = trackingViewTarget;
          const isKnown = item.party && item.party !== 'custom';
          return (
            <Modal
              title="追蹤碼內容"
              onClose={() => setTrackingViewTarget(null)}
              size="md"
              footer={
                <Button variant="secondary" size="sm" onClick={() => setTrackingViewTarget(null)}>關閉</Button>
              }
            >
              <div className="tracking-detection" style={{ marginBottom: '12px' }}>
                <span className={`tracking-badge ${isKnown ? 'tracking-badge--party' : 'tracking-badge--party-custom'}`}>{item.party || 'custom'}</span>
                <span className="tracking-badge tracking-badge--event">{item.event || 'impression'}</span>
                <span className="tracking-badge tracking-badge--type">{detected.tagType}</span>
              </div>
              <pre className="tracking-tag-view">{item.tag_content}</pre>
              <div className="tracking-copy-btn">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    navigator.clipboard.writeText(item.tag_content || '').then(() => {
                      addToast('已複製到剪貼簿', 'success');
                    }).catch(() => addToast('複製失敗', 'error'));
                  }}
                >複製</Button>
              </div>
            </Modal>
          );
        })()}
      </div>
    );
  }

  function renderFormField(compKey, prop) {
    if (prop.key.toLowerCase().includes('third_party_tracking')) {
      return renderTrackingField(compKey, prop);
    }

    const items = formValues[compKey]?.[prop.key] || [];
    const isTracker = prop.key.toLowerCase().includes('track');

    // Parse column definitions from meta_list
    const formColumns = parseColumns(prop.columns, []);

    const addItem = () => {
      const newItem = {};
      if (formColumns.length > 0) {
        for (const col of formColumns) {
          newItem[col.key] = col.default || '';
        }
      } else if (isTracker) {
        Object.assign(newItem, { party: '', event: 'impression', tag_content: '' });
      }
      updateValue(compKey, prop.key, [...items, newItem]);
    };

    const removeItem = (idx) => {
      updateValue(
        compKey,
        prop.key,
        items.filter((_, i) => i !== idx)
      );
    };

    const updateItem = (idx, field, val) => {
      const newItems = [...items];
      newItems[idx] = { ...newItems[idx], [field]: val };
      updateValue(compKey, prop.key, newItems);
    };

    return (
      <div className="repeatable-group">
        {items.map((item, idx) => (
          <div className="repeatable-item" key={idx}>
            <div className="repeatable-item-fields">
              <div className="repeatable-item-row">
                {formColumns.length > 0 ? (
                  <>
                    {formColumns.map((col) => (
                      col.type === 'select' && col.options?.length > 0 ? (
                        <select
                          key={col.key}
                          value={item[col.key] || col.default || ''}
                          onChange={(e) => updateItem(idx, col.key, e.target.value)}
                        >
                          <option value="">{col.label}</option>
                          {col.options.map((opt) => (
                            <option key={typeof opt === 'object' ? opt.value : opt}
                                    value={typeof opt === 'object' ? opt.value : opt}>
                              {typeof opt === 'object' ? opt.label : opt}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <input
                          key={col.key}
                          type="text"
                          placeholder={col.label}
                          value={item[col.key] || ''}
                          onChange={(e) => updateItem(idx, col.key, e.target.value)}
                        />
                      )
                    ))}
                  </>
                ) : (
                  // No column definition — render keys from item
                  Object.keys(item).map((itemKey) => (
                    <input
                      key={itemKey}
                      type="text"
                      placeholder={itemKey}
                      value={item[itemKey] || ''}
                      onChange={(e) => updateItem(idx, itemKey, e.target.value)}
                    />
                  ))
                )}
              </div>
            </div>
            <button className="btn-remove-item" onClick={() => removeItem(idx)}>
              <Icon name="x" size={14} />
            </button>
          </div>
        ))}
        <button className="btn-add-item" onClick={addItem}>
          + 新增{isTracker ? '追蹤碼' : '項目'}
        </button>
      </div>
    );
  }

  // Parse meta_list column definitions into flat array
  // column format: [{ colKey1: { type, description, ... }, colKey2: { ... } }, ...]
  // Group all visible properties by category, preserving component grouping
  function getGroupedProperties() {
    const groups = {};
    for (const key of CATEGORY_ORDER) groups[key] = [];

    for (const [compKey, comp] of Object.entries(components)) {
      // Get the template's field_config for this component
      const tpl = templates.find((t) => t.uuid === selectedTemplateUUID);
      const fieldConfig = tpl?.field_config || {};

      for (const prop of comp.properties) {
        const fc = fieldConfig[`${compKey}.${prop.key}`];
        const cat = categorisePropertyWithConfig(prop, fc);
        if (groups[cat]) {
          groups[cat].push({ compKey, componentName: comp.componentName, prop });
        } else {
          groups['Advance'].push({ compKey, componentName: comp.componentName, prop });
        }
      }
    }

    return groups;
  }

  // Group items within a category by componentName for sub-headers
  function groupByComponent(items) {
    const result = [];
    let currentComp = null;

    for (const item of items) {
      if (item.componentName !== currentComp) {
        result.push({ componentName: item.componentName, fields: [item] });
        currentComp = item.componentName;
      } else {
        result[result.length - 1].fields.push(item);
      }
    }

    return result;
  }

  // --- Main render ---

  if (loading) {
    return (
      <div className={`creative-editor-page${sidebarCollapsed ? ' creative-editor-page--collapsed' : ''}`}>
        <div className="creative-loading">載入中...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`creative-editor-page${sidebarCollapsed ? ' creative-editor-page--collapsed' : ''}`}>
        <div className="creative-error">
          <p>{error}</p>
          <button className="btn btn-secondary" onClick={() => navigate(`/ads/${id}?tab=creatives`)}>
            返回廣告詳情
          </button>
        </div>
      </div>
    );
  }

  const grouped = getGroupedProperties();
  const hasComponents = Object.keys(components).length > 0;
  return (
    <div className={`creative-editor-page${sidebarCollapsed ? ' creative-editor-page--collapsed' : ''}`}>
      <div className="editor-header">
        <div className="editor-header-left">
          <nav className="breadcrumb">
            <a href="#" onClick={(e) => { e.preventDefault(); navigate('/ads'); }}>廣告管理</a>
            <span className="sep">›</span>
            <a href="#" onClick={(e) => { e.preventDefault(); navigate(`/ads/${id}?tab=creatives`); }}>
              {ad?.name || `廣告 #${id}`}
            </a>
            <span className="sep">›</span>
            <span className="current">{ad?.creative_uuid ? '編輯素材' : '建立素材'}</span>
          </nav>
        </div>
        <div className="editor-header-right">
          {selectedTemplateUUID && templates.length > 0 && (
            <span className="header-template-info">
              模板：{templates.find((t) => t.uuid === selectedTemplateUUID)?.name || ''}
              {/* AS-171: show selected variant name in header (resolved from key) */}
              {parsedVariants.length > 1 && (() => {
                const displayVariant = parsedVariants.find((v) => v.variant_key === selectedVariantKey);
                const displayName = displayVariant?.name || '';
                return displayName ? (
                  <span className="header-variant-badge"> / {displayName}</span>
                ) : null;
              })()}
            </span>
          )}
          <button className="btn btn-md btn-secondary" onClick={() => navigate(`/ads/${id}?tab=creatives`)}>取消</button>
          <button className="btn btn-md btn-primary" onClick={handleSave} disabled={saving || !selectedTemplateUUID}>
            {saving ? '儲存中...' : '儲存素材'}
          </button>
        </div>
      </div>

      <div className="creative-editor-layout">
      <div className="creative-editor-content">

        {/* Template selector */}
        <div className="template-selector">
          <label>模板 (Template)</label>
          {templates.length === 0 ? (
            <div className="no-templates-msg">
              此版位類型沒有可用的模板，請聯繫管理員設定。
            </div>
          ) : (
            <select
              value={selectedTemplateUUID}
              onChange={(e) => handleTemplateChange(e.target.value)}
            >
              <option value="">請選擇模板</option>
              {templates.map((tpl) => (
                <option key={tpl.uuid} value={tpl.uuid}>
                  {tpl.name || tpl.uuid}
                </option>
              ))}
            </select>
          )}
        </div>

        {/* AS-171: Variant selector — shown when template has multiple variants (new-ad mode)
            or as a disabled label when editing an existing ad.
            New-ad: value=variant_key, label=clean name (no （預設） suffix — Admin-only).
            Edit mode: resolve current name by ad.variant_key; fallback to ad.variant_name
            snapshot (disabled) when variant_key is null/not found (legacy ad). */}
        {selectedTemplateUUID && parsedVariants.length > 1 && !editUUID && (
          <div className="template-selector">
            <label>版本 (Variant)</label>
            <select
              value={selectedVariantKey}
              onChange={(e) => handleVariantChange(e.target.value)}
            >
              {parsedVariants.map((v) => (
                <option key={v.variant_key || v.name} value={v.variant_key}>
                  {v.name}{v.description ? ` — ${v.description}` : ''}
                </option>
              ))}
            </select>
          </div>
        )}
        {selectedTemplateUUID && editUUID && (() => {
          // AS-171 edit mode: show a disabled select with the resolved or fallback variant name.
          // Resolve: find variant in parsedVariants by ad.variant_key (reflects renames).
          // Fallback: use ad.variant_name snapshot when variant_key is null or not in variants.
          if (!ad?.variant_key && !ad?.variant_name) return null;
          const resolvedVariant = ad?.variant_key
            ? parsedVariants.find((v) => v.variant_key === ad.variant_key)
            : null;
          const displayName = resolvedVariant
            ? resolvedVariant.name
            : (ad?.variant_name || '');
          if (!displayName) return null;
          return (
            <div className="template-selector">
              <label>版本 (Variant)</label>
              <select value={displayName} disabled aria-label="版本（不可更改）">
                <option value={displayName}>{displayName}</option>
              </select>
            </div>
          );
        })()}

        {/* AS-210: optional creative display name input */}
        <div className="creative-name-field">
          <Input
            label="素材名稱（選填）"
            id="creative-name"
            value={creativeName}
            onChange={(e) => setCreativeName(e.target.value)}
            placeholder={`預設：${(ad?.name || '廣告') + ' 素材'}`}
            disabled={saving}
          />
        </div>

        {/* AS-220: Case (b) — parse succeeded but all fields are hidden (visible=false in field_config).
            Show a blocking error. Do NOT fall back to JSON textarea or show any fields. */}
        {selectedTemplateUUID && !hasComponents && allFieldsHidden && (
          <div className="template-selector">
            <div className="raw-fallback__hint">
              此樣板尚未設定可顯示欄位，請聯繫管理員。
            </div>
          </div>
        )}

        {/* AS-220: Case (a) — true parse failure (config null / no config.template / meta_list issue).
            Show original raw JSON textarea fallback. */}
        {selectedTemplateUUID && !hasComponents && !allFieldsHidden && (
          <div className="template-selector">
            <div className="raw-fallback__hint">
              無法解析此模板的欄位結構，請直接編輯 JSON 設定
            </div>
            <label>素材設定 (config_object JSON)</label>
            <textarea
              rows={8}
              value={formValues._raw || '{\n  "properties": {}\n}'}
              onChange={(e) =>
                setFormValues((prev) => ({ ...prev, _raw: e.target.value }))
              }
              placeholder='{"properties": {}}'
            />
          </div>
        )}

        {/* Grouped sections */}
        {hasComponents && (
          <div className="form-sections">
          {CATEGORY_ORDER.map((cat) => {
            const items = grouped[cat];
            if (!items || items.length === 0) return null;
            const cfg = CATEGORY_CONFIG[cat];
            const componentGroups = groupByComponent(items);
            const isOpen = openSections[cat];

            return (
              <div className={`form-section ${isOpen ? 'open' : ''}`} key={cat}>
                <div className="form-section__header" onClick={() => toggleSection(cat)}>
                  <div className="form-section__icon">{cfg.icon}</div>
                  <div className="form-section__title-group">
                    <div className="form-section__title">
                      {cfg.label}
                      <span className="form-section__count">{items.length} 個欄位</span>
                    </div>
                    <div className="form-section__subtitle">{cfg.subtitle}</div>
                  </div>
                  <div className="form-section__chevron">
                    <Icon name="chevron-down" size={16} />
                  </div>
                </div>
                {isOpen && (
                  <div className="form-section__body">
                    {componentGroups.map((group) => (
                      <div key={group.componentName} className="component-group">
                        {group.fields.map(({ compKey, prop }) =>
                          renderField(compKey, prop)
                        )}
                      </div>
                    ))}
                    {cat === 'URLs' && (() => {
                      if (mediasFields.length === 0) return null;
                      return mediasFields.map(({ compKey, prop }) => {
                        const items = formValues[compKey]?.[prop.key] || [];
                        if (items.length === 0) return null;
                        return items.map((item, idx) => (
                          <div className="creative-field" key={`${compKey}-${prop.key}-click-${idx}`}>
                            <label className="creative-field-label">
                              素材 {idx + 1} 點擊連結
                            </label>
                            <input
                              type="url"
                              placeholder="https://..."
                              value={item[CLICK_URL_KEY] || ''}
                              onChange={(e) => {
                                const newItems = [...items];
                                newItems[idx] = { ...newItems[idx], [CLICK_URL_KEY]: e.target.value };
                                updateValue(compKey, prop.key, newItems);
                              }}
                            />
                          </div>
                        ));
                      });
                    })()}
                  </div>
                )}
              </div>
            );
          })}
          </div>
        )}

        {/* Actions moved to sticky header */}
      </div>

      {/* Alpha Converter Modal */}
      {alphaConverterOpen && alphaConverterCtx && (
        <Suspense fallback={null}>
          <AlphaConverterModal
            onClose={() => {
              setAlphaConverterOpen(false);
              setAlphaConverterCtx(null);
            }}
            onComplete={(file) => {
              const { compKey, propKey } = alphaConverterCtx;
              setAlphaConverterOpen(false);
              setAlphaConverterCtx(null);
              handleFileUpload(file, compKey, propKey, undefined, 'video');
            }}
          />
        </Suspense>
      )}

      {/* Preview Panel */}
      {selectedTemplateUUID && rawTemplateText && (
        <div className="creative-preview-panel">
          <div className="creative-preview-panel__header">
            <span>預覽</span>
            <button className="btn btn-text btn-sm" onClick={sendPreview}>重新整理預覽</button>
          </div>
          <div className="creative-preview-content" ref={previewContainerRef}>
            <iframe
              ref={previewIframeRef}
              src={PREVIEW_URL}
              title="Creative Preview"
              className="creative-preview-iframe"
              frameBorder="0"
              onLoad={sendPreview}
            />
          </div>
        </div>
      )}
      </div>
    </div>
  );
}
